import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Tag, Tooltip } from '~/components';
import { Avatar } from '~/components/Avatar';
import { IconButton } from '~/components/IconButton';
import { Popover } from '~/components/Popover';
import { cn } from '~/lib/cn';
import { ArtistCrossoverIcon } from '~/modules/artist-roster/components/ArtistCrossoverIcon';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ArtistContactInfo } from '../components/ArtistContactInfo/ArtistContactInfo';
import type { ArtistTableMetaType, ArtistType } from '../lib/types';
import { useIsContactInfoEnabled } from './useIsContactInfoEnabled';

const AVATAR_CLASSES =
  'size-[2.875rem] outline outline-2 bg-emphasis-subtle outline-emphasis-subtle';

export function useArtistColumns() {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const columnHelper = useMemo(() => createColumnHelper<ArtistType>(), []);
  const isArtistContactsEnabled = useIsContactInfoEnabled();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        id: 'name',
        header: () => 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-md">
            {row.original.image ? (
              <img
                src={row.original.image}
                alt={row.original.name}
                className={cn('rounded-full', AVATAR_CLASSES)}
                loading="lazy"
              />
            ) : (
              <Avatar
                name={row.original.name}
                variant="base"
                className={AVATAR_CLASSES}
              />
            )}
            <div className="line-clamp-2 text-headline-sm font-bold leading-tight group-hover/link:underline">
              {row.original.name}
            </div>
            {row.original.isCrossover ? <ArtistCrossoverIcon /> : null}
          </div>
        ),
      }),
      columnHelper.accessor('labels', {
        id: 'labels',
        header: () => 'Label(s)',
        cell: ({ row }) =>
          !!row.original.labels.length && (
            <div className="flex gap-md">
              <span className="text-body-md font-normal">
                {row.original.labels[0].name}
              </span>
              {row.original.labels.length > 1 && (
                <Tooltip
                  content={row.original.labels
                    .slice(1)
                    .map((label) => label.name)
                    .join(', ')}
                  delayDuration={0}
                  side="top"
                  sideOffset={8}
                  triggerAsChild
                  size="sm"
                >
                  <span className="cursor-pointer text-body-sm font-semibold underline decoration-1 underline-offset-4 decoration-subtle">
                    {t('labels.moreLink', {
                      count: row.original.labels.length - 1,
                    })}
                  </span>
                </Tooltip>
              )}
            </div>
          ),
      }),
      columnHelper.accessor('reports', {
        id: 'reports',
        header: () => 'Data tools',
        cell: ({ row, table }) => {
          return (
            <div className="flex flex-row gap-sm">
              {row.original?.reports.map((q) => {
                return (
                  <a
                    key={q.name}
                    href={q.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();

                      const meta = table.options?.meta as
                        | ArtistTableMetaType
                        | undefined;
                      trackEvent('rosterTableArtistInteraction', {
                        artist_id: row.original.partyId,
                        artist_name: row.original.name,
                        click_type: q.name,
                        roster_label_filters_count: meta?.filtersCount ?? 0,
                        roster_crossover_filter_applied:
                          meta?.crossoverFilterApplied ?? false,
                        roster_label_filters_applied:
                          meta?.filtersApplied ?? false,
                        roster_status: meta?.rosterStatus ?? 'all',
                      });
                    }}
                  >
                    <Tag variant="secondary">
                      {q.displayName ?? q.name}
                      <Icon
                        type="external-link"
                        size="sm"
                        variant="light"
                        role="presentation"
                        aria-hidden
                        aria-label="external-url-icon"
                        className="-mr-px text-white"
                      />
                    </Tag>
                  </a>
                );
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor('socials', {
        id: 'socials',
        header: () => 'Socials',
        meta: {
          className: 'w-[16%]',
        },
        cell: ({ row, table }) => {
          return (
            <div className="-ml-md flex items-center">
              {row.original?.socials?.map((s) => {
                return (
                  <IconButton
                    key={`${s.name}_${s.url}`}
                    iconType={s.name}
                    className="p-lg leading-none duration-300 hover:bg-emphasis-strong"
                    iconVariant="brands"
                    size="medium"
                    asLink={true}
                    linkTo={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    onClick={(e) => {
                      e.stopPropagation();

                      const meta = table.options?.meta as
                        | ArtistTableMetaType
                        | undefined;

                      trackEvent('rosterTableArtistInteraction', {
                        artist_id: row.original.partyId,
                        artist_name: row.original.name,
                        click_type: s.name,
                        roster_label_filters_count: meta?.filtersCount ?? 0,
                        roster_crossover_filter_applied:
                          meta?.crossoverFilterApplied ?? false,
                        roster_label_filters_applied:
                          meta?.filtersApplied ?? false,
                        roster_status: meta?.rosterStatus ?? 'all',
                      });
                    }}
                  />
                );
              })}
            </div>
          );
        },
      }),
      ...(isArtistContactsEnabled
        ? [
            columnHelper.display({
              id: 'contactInfo',
              enableSorting: false,
              header: () => null,
              meta: {
                className: 'w-[4%]',
              },
              cell: ({ row }) => (
                <Popover
                  side="left"
                  sideOffset={6}
                  align="start"
                  content={<ArtistContactInfo partyId={row.original.partyId} />}
                  stopPropagation
                  triggerAsChild
                >
                  <button
                    aria-label={t('labels.contactInfo')}
                    className="rounded-full border p-sm transition-colors border-transparent hover:bg-emphasis-subtle hover:border-emphasis-strong"
                  >
                    <Tooltip
                      content={t('labels.contactInfo')}
                      delayDuration={0}
                      sideOffset={12}
                      side="top"
                      size="sm"
                      triggerAsChild
                    >
                      <div className="flex size-md items-center justify-center">
                        <Icon
                          type="ellipsis-vertical"
                          variant="regular"
                          size="sm"
                        />
                      </div>
                    </Tooltip>
                  </button>
                </Popover>
              ),
            }),
          ]
        : []),
    ];
  }, [columnHelper, t, trackEvent, isArtistContactsEnabled]);

  return { columns };
}
